import trajectory from './trajectory.ts'
import { distance, gaussianRandom, chiRandom } from './stats.js'
import ipsc from './ipsc.js'
import isPointInsidePolygon from './pointInPolygon.js'
import { average, standardDeviation } from './stats.js'

export default function simulate (profile, variables, constraints = {}, maxSims = 1000) {
  let hits1 = 0
  let hits2 = 0

  const actualOptions = {
    ...profile,
    wind: [ { speed: 0, direction: 0 } ],
    useAerodynamicJump: true,
    useSpinDrift: true
  }
  let iterations = 1
  let target
  let groupSizeSd
  let vertZero
  let horizZero
  variables.forEach(variable => {
    if (variable.name === 'iterations') {
      iterations = parseInt(variable.actual)
    } else if (variable.name === 'groupSize') {
      groupSizeSd = variable.sd
    } else if (variable.name === 'horizontalZero') {
      horizZero = variable.actual
    } else if (variable.name === 'verticalZero') {
      vertZero = variable.actual
    } else if (variable.name === 'target') {
      target = variable
    } else if (variable.actual) {
      if (variable.name === 'windSpeed') {
        actualOptions.wind[0].speed = variable.actual
      } else if (variable.name === 'windDirection') {
        actualOptions.wind[0].direction = variable.actual
      } else {
        actualOptions[variable.name] = variable.actual
      }
    }
  })

  const t = trajectory(actualOptions)
  const actualSolution = t[actualOptions.range]
  if (constraints.targetEnergy && actualSolution.energy < parseFloat(constraints.targetEnergy.min)) return false
  if (constraints.targetEnergy && actualSolution.energy > parseFloat(constraints.targetEnergy.max)) return false
  if (constraints.targetVelocity && actualSolution.velocity < parseFloat(constraints.targetVelocity.min)) return false
  if (constraints.targetVelocity && actualSolution.velocity > parseFloat(constraints.targetVelocity.max)) return false

  const points1 = []
  const points2 = []
  for (let i = 0; i < Math.min(iterations, maxSims); ++i) {
    const options1 = { ...actualOptions }
    const options2 = { ...actualOptions }
    variables.forEach(variable => {
      if (variable.measured) {
        let value1
        let value2
        if (variable.sd) {
          value1 = gaussianRandom(parseFloat(variable.sd), parseFloat(variable.measured))
          value2 = gaussianRandom(parseFloat(variable.sd), parseFloat(variable.measured))
        } else {
          value1 = parseFloat(variable.measured)
          value2 = parseFloat(variable.measured)
        }
        if (variable.name === 'range') {
          value1 = Math.max(10, Math.round(value1))
        }
        if (variable.name === 'windSpeed') {
          options1.wind[0].speed = value1
          options2.wind[0].speed = value1
        } else if (variable.name === 'windDirection') {
          options1.wind[0].direction = value1
          options2.wind[0].direction = value1
        } else {
          options1[variable.name] = value1
          if (['v0', 'bc'].includes(variable.name)) {
            options2[variable.name] = value2
          } else {
            options2[variable.name] = value1
          }
        }
      } else if (typeof variable.using === 'boolean') {
        options1[variable.name] = variable.using
        options2[variable.name] = variable.using
      }
    })
    const getXY = (options, groupSizeSd, adjustX, adjustY, horizZero, vertZero, target) => {
      const tn = trajectory(options)
      const solution = tn[options.range];

      const angle = Math.random() * 2 * Math.PI;
      const scale = options.range * 1.047 / 100
      const dispersionDistance = chiRandom(groupSizeSd * scale)
      const x = actualSolution.wind - solution.wind + dispersionDistance * Math.cos(angle) + horizZero * scale - adjustX
      const y = actualSolution.drop - solution.drop + dispersionDistance * Math.sin(angle) + vertZero * scale - adjustY
      const d = distance(0, 0, x, y)
      let hit = hitTarget(target, d, x, y)
      return { t: tn, x, y, d, hit }
    }
    const t1 = getXY(options1, groupSizeSd, 0, 0, parseFloat(horizZero), parseFloat(vertZero), target)
    const t2 = getXY(options2, groupSizeSd, t1.x, t1.y, parseFloat(horizZero), parseFloat(vertZero), target)
    if (t1.hit) ++hits1
    if (t2.hit) ++hits2
    points1.push([ t1.x, t1.y ])
    points2.push([ t2.x, t2.y ])
  }
  const sd1x = standardDeviation(points1.map(v => v[0]))
  const sd1y = standardDeviation(points1.map(v => v[1]))
  const sd2x = standardDeviation(points2.map(v => v[0]))
  const sd2y = standardDeviation(points2.map(v => v[1]))
  const avg1x = average(points1.map(v => v[0]))
  const avg1y = average(points1.map(v => v[1]))
  const avg2x = average(points2.map(v => v[0]))
  const avg2y = average(points2.map(v => v[1]))
  let fakeSims = iterations - Math.min(iterations, maxSims)
  for (let i = 0; i < fakeSims; ++i) {
    const x1 = gaussianRandom(sd1x, avg1x)
    const y1 = gaussianRandom(sd1y, avg1y)
    const d1 = distance(0, 0, x1, y1)
    points1.push([ x1, y1 ])
    let hit1 = hitTarget(target, d1, x1, y1)
    if (hit1) ++hits1

    const x2 = gaussianRandom(sd2x, avg2x)
    const y2 = gaussianRandom(sd2y, avg2y)
    const d2 = distance(0, 0, x2, y2)
    points2.push([ x2, y2 ])
    let hit2 = hitTarget(target, d2, x2, y2)
    if (hit2) ++hits2
  }
  const prob1 = hits1 / points1.length
  const prob2 = hits2 / points2.length
  return { points1, points2, hits1, hits2, prob1, prob2 }
}

function hitTarget(target, d, x, y) {
  let hit = false
  if (target.shape === 'circle') {
    hit = d <= target.size / 2
  } else if (target.shape === 'square') {
    hit = Math.abs(x) < target.size / 2 && Math.abs(y) < target.size / 2
  } else if (target.shape === 'ipsc') {
    hit = isPointInsidePolygon([ x, y ], ipsc)
  } else {
    throw new Error('invalid target shape')
  }
  return hit
}
